import React, { useCallback } from "react";
import SidebarSection from "domains/inference/components/InferenceGenerator/Sidebar/Section";
import { useCreateBlendContext } from "domains/models/contexts/CreateBlendProvider";
import { TOP_BAR_HEIGHT } from "domains/navigation/constants/TopBar";
import useRouter from "domains/navigation/hooks/useRouter";
import Button from "domains/ui/components/Button";
import ButtonWithModal from "domains/ui/components/ButtonWithModal";
import { ButtonSelect } from "domains/ui/components/ControlButtonSelect";
import Divider from "domains/ui/components/Divider";
import Icon from "domains/ui/components/Icon";
import ScenarioInput from "domains/ui/components/ScenarioInput";
import TourGuideIcon from "domains/ui/components/TourGuideIcon";
import WithLabelAndTooltip from "domains/ui/components/WithLabelAndTooltip";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";

import { HStack, SimpleGrid, Text, Tooltip, VStack } from "@chakra-ui/react";

import BlendConceptSliders from "./BlendConceptSlider";
import BlendModalTestConcept from "./BlendModalTestConcept";

interface BlendLayoutProps {
  contentScrollRef?: React.RefObject<HTMLDivElement>;
  children: React.ReactNode;
}

export default function BlendLayout({
  contentScrollRef,
  children,
}: BlendLayoutProps) {
  const router = useRouter();
  const {
    type,
    setType,
    name,
    setName,
    concepts,
    setIsTryOutModalOpen,
    getModel,
    isSaveLoading,
    setIsSaveLoading,
    isSaveDisabled,
  } = useCreateBlendContext();

  const handleCreateComposition = useCallback(async () => {
    if (isSaveDisabled) return;

    setIsSaveLoading(true);
    const model = await getModel();

    if (model) {
      Track(AnalyticsEvents.Blend.Created, { concepts: model.concepts });
      void router.push({
        pathname: "/models/[id]",
        query: { id: model.id },
      });
    }

    setIsSaveLoading(false);
  }, [isSaveDisabled, setIsSaveLoading, getModel, router]);

  const getButtonTooltips = () => {
    if (!concepts || concepts.length < 1) {
      return "Please add at least one model";
    } else if (name.length < 1) {
      return "Please name your model";
    } else {
      return undefined;
    }
  };

  return (
    <>
      <VStack overflow="hidden" h="100vh" spacing={0}>
        <HStack
          justify="space-between"
          w="100%"
          h={`${TOP_BAR_HEIGHT}px`}
          px={6}
          borderBottomWidth={1}
          bgColor="backgroundTertiary.500"
          data-group
        >
          <HStack spacing={2}>
            <ButtonWithModal
              variant="ghost"
              size="sm"
              p={1.5}
              ml={-2}
              modalHeader="You're about to leave model composition."
              modalBody="Unsaved changes will be lost."
              modalConfirmMessage="OK"
              onConfirm={() => {
                void router.push({
                  pathname: "/assets",
                  query: { tab: "model" },
                });
              }}
            >
              <Icon id="Ui/Cross" color="textPrimary" height="12px" />
            </ButtonWithModal>

            <Text size="body.bold.lg">Compose a Model</Text>
            <TourGuideIcon
              guide="composition-intro"
              opacity={0}
              _groupHover={{
                opacity: 1,
              }}
              mt="1px"
            />
          </HStack>
        </HStack>

        <HStack
          align="start"
          w="full"
          h={`calc(100% - ${TOP_BAR_HEIGHT}px)`}
          spacing={0}
        >
          <VStack
            align="stretch"
            flexShrink={0}
            overflow="auto"
            w="375px"
            h="full"
            pb={6}
            borderRightWidth={1}
            bgColor="backgroundTertiary.500"
            spacing={0}
          >
            <SidebarSection>
              <WithLabelAndTooltip direction="column" label="Name">
                <ScenarioInput
                  value={name}
                  setValue={setName}
                  placeholder="Model Name"
                />
              </WithLabelAndTooltip>
            </SidebarSection>
            <Divider />

            <SidebarSection>
              <WithLabelAndTooltip direction="column" label="Base Model">
                <SimpleGrid columns={2} spacing={2}>
                  <ButtonSelect
                    isSelected={type === "flux.1"}
                    onClick={() => setType("flux.1")}
                    isDisabled={concepts.length > 0}
                  >
                    Flux
                  </ButtonSelect>

                  <ButtonSelect
                    isSelected={type === "sd-xl"}
                    onClick={() => setType("sd-xl")}
                    isDisabled={concepts.length > 0}
                  >
                    SDXL
                  </ButtonSelect>
                </SimpleGrid>
              </WithLabelAndTooltip>
            </SidebarSection>
            <Divider />

            <BlendConceptSliders />
            <Divider />

            <SidebarSection>
              <Tooltip label={getButtonTooltips()}>
                <VStack align="stretch">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setIsTryOutModalOpen(true);
                    }}
                    size="sm"
                    isLoading={isSaveLoading}
                    isDisabled={isSaveDisabled}
                  >
                    Test
                  </Button>

                  <Button
                    onClick={handleCreateComposition}
                    isLoading={isSaveLoading}
                    isDisabled={isSaveDisabled}
                    size="sm"
                  >
                    Save Composition
                  </Button>
                </VStack>
              </Tooltip>
            </SidebarSection>
            <Divider />
          </VStack>

          <VStack ref={contentScrollRef} overflow="auto" w="100%" h="100%">
            {children}
          </VStack>
        </HStack>
      </VStack>

      <BlendModalTestConcept />
    </>
  );
}
